/**
 * Pasture detail page — shared between v1 and v2 (no LayoutPicker).
 *
 * The page stitches together three pieces:
 *   1. Read-only summary card (status, acres, fencing, water, capacity)
 *   2. Photos card with EntityImageGallery — lets the user attach
 *      images to the pasture (gates, fence repairs, water sources)
 *   3. Edit + Archive controls via the generic v2 dialogs
 *
 * `last_rotation_at` is intentionally NOT in the edit FIELDS — that
 * column is meant to be set automatically when a rotation log entry is
 * recorded, not typed by hand.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Edit2, Fence, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { EntityImageGallery } from "@/components/EntityImageGallery";
import { EditEntityDialog } from "@/components/v2/dialogs/EditEntityDialog";
import { DeleteEntityDialog } from "@/components/v2/dialogs/DeleteEntityDialog";
import type { FieldDef } from "@/components/v2/dialogs/AddEntityDialog";
import { authFetch } from "@/lib/auth";

type Status = "active" | "resting" | "closed";

interface Pasture {
  id: string;
  ranch_id: string;
  name: string;
  status: Status | "empty";
  acres: number | null;
  acreage?: number | null;
  fencing_status: "good" | "fair" | "poor" | "unknown" | null;
  water_source: string | null;
  carrying_capacity: number | null;
  last_rotation_at: string | null;
  last_rotation?: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<Status, string> = {
  active: "Active",
  resting: "Resting",
  closed: "Closed",
};
const STATUS_TONE: Record<Status, string> = {
  active: "bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark",
  resting: "bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark",
  closed: "bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark",
};

const FIELDS: FieldDef[] = [
  { name: "name", label: "Name", required: true, placeholder: "North Pasture" },
  { name: "acres", label: "Acres", type: "number", half: true, placeholder: "40" },
  {
    name: "status",
    label: "Status",
    type: "select",
    half: true,
    options: [
      { value: "active", label: "Active" },
      { value: "resting", label: "Resting" },
      { value: "closed", label: "Closed" },
    ],
  },
  {
    name: "fencing_status",
    label: "Fencing",
    type: "select",
    half: true,
    options: [
      { value: "good", label: "Good" },
      { value: "fair", label: "Fair" },
      { value: "poor", label: "Poor" },
      { value: "unknown", label: "Unknown" },
    ],
  },
  { name: "water_source", label: "Water source", half: true, placeholder: "Creek / well / tank" },
  { name: "carrying_capacity", label: "Carrying capacity", type: "number" },
  { name: "notes", label: "Notes", type: "textarea" },
];

function transformPasture(v: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (v.name !== undefined) out.name = v.name;
  if (v.acres !== undefined) out.acres = v.acres ? Number(v.acres) : null;
  if (v.status !== undefined) out.status = v.status || "active";
  if (v.fencing_status !== undefined) out.fencing_status = v.fencing_status || null;
  if (v.water_source !== undefined) out.water_source = v.water_source || null;
  if (v.carrying_capacity !== undefined)
    out.carrying_capacity = v.carrying_capacity ? Number(v.carrying_capacity) : null;
  if (v.notes !== undefined) out.notes = v.notes || null;
  return out;
}

function statusOf(p: Pasture): Status {
  if (p.status === "empty") return "closed";
  if (p.status === "active" || p.status === "resting" || p.status === "closed") return p.status;
  return "active";
}

function acresOf(p: Pasture): number | null {
  return p.acres ?? p.acreage ?? null;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  try {
    const iso = value.includes("T") ? value : `${value}T00:00:00`;
    return format(parseISO(iso), "MMM d, yyyy");
  } catch {
    return value;
  }
}

export function PastureDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pasture, setPasture] = useState<Pasture | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const loadPasture = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const data = await authFetch<Pasture>(`/api/pastures/${id}`);
      setPasture(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load pasture");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadPasture();
  }, [loadPasture, refreshTick]);

  if (loading) {
    return (
      <>
        <PageHeader
          title=""
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Pastures", href: "/pastures" },
            { label: "Loading…" },
          ]}
        />
        <Skeleton className="h-48 w-full rounded-lg" />
      </>
    );
  }

  if (error || !pasture) {
    return (
      <>
        <PageHeader
          title="Pasture Not Found"
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Pastures", href: "/pastures" },
            { label: "Error" },
          ]}
        />
        <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
          {error || "Pasture not found"}
        </div>
      </>
    );
  }

  const status = statusOf(pasture);
  const acres = acresOf(pasture);
  const lastRotation = pasture.last_rotation_at ?? pasture.last_rotation ?? null;

  return (
    <>
      <PageHeader
        title={pasture.name}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Pastures", href: "/pastures" },
          { label: pasture.name },
        ]}
      >
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <Edit2 className="mr-1 h-4 w-4" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDeleteOpen(true)}
          className="text-destructive border-destructive/30"
        >
          <Trash2 className="mr-1 h-4 w-4" /> Archive
        </Button>
      </PageHeader>

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Fence className="h-5 w-5 text-ranch-green" /> Pasture Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="mt-1">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[status]}`}
                  >
                    {STATUS_LABEL[status]}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Acres</p>
                <p className="font-medium">{acres != null ? acres.toLocaleString() : "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fencing</p>
                <p className="font-medium capitalize">{pasture.fencing_status ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Water source</p>
                <p className="font-medium">{pasture.water_source ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Carrying capacity</p>
                <p className="font-medium">{pasture.carrying_capacity ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last rotated</p>
                <p className="font-medium">{formatDate(lastRotation)}</p>
              </div>
              {pasture.notes ? (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{pasture.notes}</p>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <EntityImageGallery entityType="pasture" entityId={pasture.id} />
          </CardContent>
        </Card>
      </div>

      <EditEntityDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title={`Edit ${pasture.name}`}
        endpoint="/api/pastures"
        entityId={pasture.id}
        fields={FIELDS}
        initialValues={{
          name: pasture.name,
          acres: acres != null ? String(acres) : "",
          status,
          fencing_status: pasture.fencing_status ?? "",
          water_source: pasture.water_source ?? "",
          carrying_capacity:
            pasture.carrying_capacity != null ? String(pasture.carrying_capacity) : "",
          notes: pasture.notes ?? "",
        }}
        transform={transformPasture}
        onSaved={() => setRefreshTick((t) => t + 1)}
      />
      <DeleteEntityDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        endpoint="/api/pastures"
        entityId={pasture.id}
        entityName={pasture.name}
        onDeleted={() => navigate("/pastures")}
      />
    </>
  );
}
